import Backend from '../backend';
import NativeOrWeb from './native';
import { emitAuthRefreshFailed, emitAuthSessionReset } from './auth-telemetry';

export const LOGOUT_PENDING_KEY = 'logoutPending';
export const LOGOUT_CONFIRMED_KEY = 'logoutConfirmed';
export const LOGOUT_RETRY_CANCEL_KEY = 'logoutRetryCancel';

export type RefreshOutcome =
  | { result: 'ok'; correlationId?: string }
  | { result: 'session_dead'; httpStatus: 401; correlationId?: string; latched: boolean }
  | {
      result: 'transient';
      reason: 'network' | 'http_403' | 'http_429' | 'http_5xx' | 'http_other';
      httpStatus?: number;
      correlationId?: string;
    }
  | { result: 'cancelled' };

let refreshPromise: Promise<RefreshOutcome> | null = null;
let sessionDeadLatch = false;
let authEpoch = 0;
let purgedThisSession = false;
let mutationTail: Promise<void> = Promise.resolve();
let logoutRetry: (() => Promise<boolean>) | null = null;
let logoutRetryListening = false;
let logoutAbort: AbortController | null = null;
let logoutRefreshSnapshot: string | undefined;

type BarrierOp = () => Promise<void>;

function enqueueBarrier(op: BarrierOp): Promise<void> {
  const run = mutationTail.then(op, op);
  mutationTail = run.catch(() => undefined);
  return run;
}

export function currentAuthEpoch(): number {
  return authEpoch;
}

export function bumpAuthEpoch(): number {
  authEpoch += 1;
  return authEpoch;
}

export function isSessionDeadLatched(): boolean {
  return sessionDeadLatch;
}

export function clearSessionDeadLatch(): void {
  sessionDeadLatch = false;
}

function scrubLegacySessionTombstone(): void {
  try {
    sessionStorage.removeItem(LOGOUT_PENDING_KEY);
  } catch (_) {
    // ignore
  }
}

if (typeof window !== 'undefined') {
  scrubLegacySessionTombstone();
}

export function isLogoutPending(): boolean {
  try {
    return localStorage.getItem(LOGOUT_PENDING_KEY) === '1';
  } catch (_) {
    return false;
  }
}

export function setLogoutPending(): void {
  try {
    sessionStorage.removeItem(LOGOUT_PENDING_KEY);
    localStorage.setItem(LOGOUT_PENDING_KEY, '1');
  } catch (_) {
    // ignore quota / private mode
  }
  bumpAuthEpoch();
}

export function clearLogoutPending(): void {
  try {
    sessionStorage.removeItem(LOGOUT_PENDING_KEY);
    localStorage.removeItem(LOGOUT_PENDING_KEY);
  } catch (_) {
    // ignore
  }
}

function storageFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch (_) {
    return false;
  }
}

function writeFlag(key: string, on: boolean): void {
  try {
    if (on) {
      localStorage.setItem(key, '1');
    } else {
      localStorage.removeItem(key);
    }
  } catch (_) {
    // ignore
  }
}

export function isLogoutConfirmed(): boolean {
  return storageFlag(LOGOUT_CONFIRMED_KEY);
}

export function markLogoutConfirmed(): void {
  writeFlag(LOGOUT_CONFIRMED_KEY, true);
  stopLogoutRetry();
}

export function clearLogoutConfirmed(): void {
  writeFlag(LOGOUT_CONFIRMED_KEY, false);
}

async function runLogoutRetry(): Promise<void> {
  if (!logoutRetry || isLogoutConfirmed() || !isLogoutPending()) {
    return;
  }
  try {
    const done = await logoutRetry();
    if (done) {
      markLogoutConfirmed();
    }
  } catch (_) {
    // best-effort
  }
}

function onOnlineLogoutRetry() {
  void runLogoutRetry();
}

export function startLogoutRetry(attempt: () => Promise<boolean>): void {
  logoutRetry = attempt;
  void runLogoutRetry();
  if (typeof window === 'undefined' || logoutRetryListening) {
    return;
  }
  window.addEventListener('online', onOnlineLogoutRetry);
  logoutRetryListening = true;
}

export function stopLogoutRetry(): void {
  logoutRetry = null;
  if (typeof window !== 'undefined' && logoutRetryListening) {
    window.removeEventListener('online', onOnlineLogoutRetry);
    logoutRetryListening = false;
  }
}

export function markPurgedThisSession(): void {
  purgedThisSession = true;
}

function classifyHttp(status: number): Extract<RefreshOutcome, { result: 'transient' }> {
  if (status === 403) return { result: 'transient', reason: 'http_403', httpStatus: status };
  if (status === 429) return { result: 'transient', reason: 'http_429', httpStatus: status };
  if (status >= 500) return { result: 'transient', reason: 'http_5xx', httpStatus: status };
  return { result: 'transient', reason: 'http_other', httpStatus: status };
}

async function persistRotatedTokens(data: { accessToken?: string; refreshToken?: string }, epochAtStart: number) {
  let cancelled = false;
  await enqueueBarrier(async () => {
    if (writeBlocked(epochAtStart)) {
      cancelled = true;
      return;
    }
    if (data.accessToken) {
      await NativeOrWeb.saveAccessTokenCookie(data.accessToken).catch(() => undefined);
    }
    if (writeBlocked(epochAtStart)) {
      cancelled = true;
      return;
    }
    if (data.refreshToken) {
      await NativeOrWeb.saveRefreshTokenCookie(data.refreshToken).catch(() => undefined);
    }
    if (writeBlocked(epochAtStart)) {
      cancelled = true;
      return;
    }
    clearSessionDeadLatch();
  });
  return cancelled;
}

function writeBlocked(epochAtRequest: number): boolean {
  return currentAuthEpoch() !== epochAtRequest || isLogoutPending() || isSessionDeadLatched();
}

export async function enqueueCredentialWrite(
  epochAtRequest: number,
  tokens: { accessToken?: string; refreshToken?: string }
): Promise<void> {
  await enqueueBarrier(async () => {
    if (writeBlocked(epochAtRequest)) {
      return;
    }
    if (tokens.accessToken) {
      await NativeOrWeb.saveAccessTokenCookie(tokens.accessToken).catch(() => undefined);
    }
    if (writeBlocked(epochAtRequest)) {
      return;
    }
    if (tokens.refreshToken) {
      await NativeOrWeb.saveRefreshTokenCookie(tokens.refreshToken).catch(() => undefined);
    }
  });
}

export async function enqueueCredentialPurge(epochAtRequest: number): Promise<void> {
  await enqueueBarrier(async () => {
    if (currentAuthEpoch() !== epochAtRequest) {
      return;
    }
    if (!logoutRefreshSnapshot && isLogoutPending()) {
      const current = await NativeOrWeb.getRefreshTokenCookie().catch(() => undefined);
      if (currentAuthEpoch() !== epochAtRequest) {
        return;
      }
      if (current) {
        logoutRefreshSnapshot = current;
      }
    }
    if (currentAuthEpoch() !== epochAtRequest) {
      return;
    }
    await NativeOrWeb.removeRefreshTokenCookie().catch(() => undefined);
    if (currentAuthEpoch() !== epochAtRequest) {
      return;
    }
    await NativeOrWeb.removeAccessTokenCookie().catch(() => undefined);
    if (currentAuthEpoch() !== epochAtRequest) {
      return;
    }
    markPurgedThisSession();
  });
}

export async function latchAndPurgeDeadSession(): Promise<void> {
  sessionDeadLatch = true;
  await enqueueCredentialPurge(currentAuthEpoch());
}

export async function enqueueLogoutLocalCleanup(
  epochAtRequest: number,
  work: () => Promise<void> | void
): Promise<void> {
  await enqueueBarrier(async () => {
    if (currentAuthEpoch() !== epochAtRequest || !isLogoutPending()) {
      return;
    }
    await work();
  });
}

async function postRefresh(body: Record<string, unknown>): Promise<{
  status: number;
  data: { accessToken?: string; refreshToken?: string };
  correlationId?: string;
}> {
  const res = await fetch(Backend.methodUrl('refresh-token'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const correlationId = res.headers && res.headers.get ? res.headers.get('x-correlation-id') || undefined : undefined;
  let data: { accessToken?: string; refreshToken?: string } = {};
  if (res.status === 200) {
    try {
      data = await res.json();
    } catch (_) {
      data = {};
    }
  }
  return { status: res.status, data, correlationId: correlationId || undefined };
}

async function runRefresh(): Promise<RefreshOutcome> {
  if (isLogoutPending()) {
    return { result: 'session_dead', httpStatus: 401, latched: true };
  }
  if (sessionDeadLatch) {
    return { result: 'session_dead', httpStatus: 401, latched: true };
  }

  const epochAtStart = currentAuthEpoch();

  if (NativeOrWeb.isNativePlatform() && purgedThisSession) {
    const readable = await NativeOrWeb.getRefreshTokenCookie();
    if (!readable) {
      return { result: 'session_dead', httpStatus: 401, latched: true };
    }
  }

  const refreshToken = await NativeOrWeb.getRefreshTokenCookie();
  const body = refreshToken ? { refreshToken } : {};

  const attempt = async (): Promise<RefreshOutcome> => {
    try {
      const { status, data, correlationId } = await postRefresh(body);
      if (status === 200) {
        const cancelled = await persistRotatedTokens(data, epochAtStart);
        if (cancelled) {
          return { result: 'cancelled' };
        }
        return { result: 'ok', correlationId };
      }
      if (status === 401) {
        if (currentAuthEpoch() !== epochAtStart || isLogoutPending()) {
          return { result: 'cancelled' };
        }
        await latchAndPurgeDeadSession();
        emitAuthRefreshFailed({
          pathClass: 'refresh',
          httpStatus: 401,
          reason: 'session_dead',
          latched: true,
          refreshCorrelationId: correlationId
        });
        return { result: 'session_dead', httpStatus: 401, correlationId, latched: true };
      }
      const transient = { ...classifyHttp(status), correlationId };
      emitAuthRefreshFailed({
        pathClass: 'refresh',
        httpStatus: status,
        reason: transient.reason,
        latched: false,
        refreshCorrelationId: correlationId
      });
      return transient;
    } catch (_) {
      emitAuthRefreshFailed({ pathClass: 'refresh', reason: 'network', latched: false });
      return { result: 'transient', reason: 'network' };
    }
  };

  const first = await attempt();
  if (first.result !== 'transient') {
    return first;
  }
  await new Promise(resolve => setTimeout(resolve, 1000));
  if (currentAuthEpoch() !== epochAtStart || isLogoutPending()) {
    return { result: 'cancelled' };
  }
  const second = await attempt();
  return second;
}

export function attemptTokenRefresh(): Promise<RefreshOutcome> {
  if (!refreshPromise) {
    refreshPromise = runRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export function resetAuthOnce(dispatchReset: () => void, isAuthenticated: boolean): void {
  if (!isAuthenticated) {
    return;
  }
  bumpAuthEpoch();
  emitAuthSessionReset({ reason: 'session_dead', latched: true });
  dispatchReset();
}

export function beginLogoutRequest(): AbortSignal | undefined {
  if (typeof AbortController === 'undefined') {
    return undefined;
  }
  if (logoutAbort) {
    logoutAbort.abort();
  }
  logoutAbort = new AbortController();
  return logoutAbort.signal;
}

export function abortInFlightLogout(): void {
  if (logoutAbort) {
    logoutAbort.abort();
    logoutAbort = null;
  }
}

export function peekLogoutRefreshSnapshot(): string | undefined {
  return logoutRefreshSnapshot;
}

export function onConfirmedLoginSuccess(): void {
  abortInFlightLogout();
  logoutRefreshSnapshot = undefined;
  stopLogoutRetry();
  clearLogoutPending();
  clearLogoutConfirmed();
  clearSessionDeadLatch();
  purgedThisSession = false;
  bumpAuthEpoch();
  try {
    localStorage.setItem(LOGOUT_RETRY_CANCEL_KEY, String(Date.now()));
  } catch (_) {
    // ignore
  }
}

export function onUserSignOut(refreshTokenSnapshot?: string): number {
  if (refreshTokenSnapshot) {
    logoutRefreshSnapshot = refreshTokenSnapshot;
  }
  setLogoutPending();
  const epoch = currentAuthEpoch();
  void enqueueCredentialPurge(epoch);
  return epoch;
}

export function __resetAuthRefreshStateForTests(): void {
  refreshPromise = null;
  sessionDeadLatch = false;
  authEpoch = 0;
  purgedThisSession = false;
  mutationTail = Promise.resolve();
  abortInFlightLogout();
  logoutRefreshSnapshot = undefined;
  stopLogoutRetry();
  try {
    sessionStorage.removeItem(LOGOUT_PENDING_KEY);
    localStorage.removeItem(LOGOUT_PENDING_KEY);
    localStorage.removeItem(LOGOUT_CONFIRMED_KEY);
    localStorage.removeItem(LOGOUT_RETRY_CANCEL_KEY);
  } catch (_) {
    // ignore
  }
}
